import { useState, useEffect, useContext } from 'react';
import { useSession } from 'next-auth/react';
import {
  Text,
  Center,
  Box,
  Button,
  useColorModeValue,
  Flex,
  Link,
} from '@chakra-ui/react';
import PuffLoader from 'react-spinners/PuffLoader';
import { useFirebaseUser } from 'utils/useFirebaseUser';
import { firebaseClient } from 'firebaseClient';
import { getAccount } from 'utils/sessionHelper';
import { isTweetFromAccount } from 'controllers/schedule';
import { TweetContext } from 'context/tweetContext';
import { TweetBar } from './tweet-bar';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import toast from 'react-hot-toast';
import { useStats } from 'hooks/useStats';
import * as analytics from 'utils/analytics';

interface Props {
  isSelected: boolean;
}

export function ClientPanel({ isSelected }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const isAuthenticated = useFirebaseUser();
  const tweetContext: any = useContext(TweetContext);
  const { data: session } = useSession() ?? {};
  const tweetbarBg = useColorModeValue('blue.50', 'blue.900');
  const threadBg = useColorModeValue('purple.100', 'purple.700');
  const { updateStatsCounter } = useStats();

  useEffect(() => {
    // console.log("ghost panel useEffect");
    if (isAuthenticated && isSelected && !hasFetched) {
      setHasFetched(true);
      loadDrafts();
      updateStatsCounter('visit_queue_ghostwriters');
    } else {
      getDrafts();
    }
  }, [isAuthenticated, tweetContext.refresh, isSelected]);

  const loadDrafts = async () => {
    setIsLoading(true);
    await getDrafts();
    setIsLoading(false);
  };

  const getDrafts = async (type?: 'loadMore') => {
    if (!isAuthenticated) {
      return;
    }

    let draftsCopy = [] as any;
    const db = firebaseClient.firestore();

    let docs = await db
      .collection('users')
      .doc(getAccount(session).id)
      .collection('tweets')
      .where('status', '==', 'ghost')
      .where('idAccount', '==', getAccount(session).idAccount)
      .orderBy('dateCreated', 'desc')
      .get();

    docs.forEach((doc) => {
      const data = doc.data();
      draftsCopy.push(data);
    });

    draftsCopy.forEach((tw) => {
      if (tw.statusGhost === 'pending') tw.priority = 3;
      else if (tw.statusGhost === 'rejected') tw.priority = 1;
      else if (tw.statusGhost === 'done') tw.priority = 0;
      else tw.priority = 2;
    });

    // sort by statusGhost
    draftsCopy = draftsCopy.sort((a, b) => b.priority - a.priority);
    // console.log('draftsCopy:', draftsCopy)
    // draftsCopy = [];

    setDrafts(draftsCopy);
  };

  const onDelete = async (docId: string) => {
    const filteredDrafts = drafts.filter((d) => d.id !== docId);
    setDrafts(filteredDrafts);
  };

  const loadMoreDrafts = async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoadingMore(true);
    await getDrafts('loadMore');
    setIsLoadingMore(false);
  };

  if (isLoading) {
    return (
      <Center mt='40'>
        <PuffLoader color={'#0A66C2'} loading={isLoading} size={50} />
      </Center>
    );
  }

  if (!isLoading && drafts.length === 0) {
    return (
      <Center mt='44'>
        <Box textAlign='center'>
          <Text mb={10} fontWeight={600}>
            No posts pushed by writers
          </Text>
          <Text mt={2}>
            Share this link with anyone so they can write for you
          </Text>
          <CopyToClipboard
            text={
              'https://app.taplio.com/write?handle=' +
              getAccount(session)?.username
            }
            onCopy={() => {
              toast.success('Link copied to clipboard');
            }}
          >
            <Button
              mt={5}
              variant='primary'
              isDisabled={getAccount(session)?.username === undefined}
            >
              Share my Custom Link
            </Button>
          </CopyToClipboard>
        </Box>
      </Center>
    );
  }

  return (
    <Box>
      {/* {
        // (session?.user?.data?.isAdmin) && (
        true && (
            <Flex justifyContent="flex-end" alignItems="center" w="100%" mt="0" pb={0}>
                <Flex>
                    <Button
                        variant="outline"
                        size="sm"
                        as={Link}
                        href="/ghostwriting"
                    >
                        Find ghostwriters (beta)
                    </Button>
                </Flex>
            </Flex>
        )
      } */}
      <Flex alignItems='center' mt='7' pb={4}>
        <Text fontSize='lg' fontWeight='700'>
          Drafts written by Ghostwriters
        </Text>
        <CopyToClipboard
          text={
            'https://app.taplio.com/write?handle=' +
            getAccount(session)?.username
          }
          onCopy={() => {
            toast.success('Link copied to clipboard');
          }}
        >
          <Button ml={5} size='sm' variant='outline' colorScheme='gray'>
            Share my Custom Link
          </Button>
        </CopyToClipboard>
      </Flex>
      {drafts.map((draft) => (
        <TweetBar
          key={draft.id}
          type='ghost'
          tweet={draft}
          // bg={tweetbarBg}
          // threadBg={threadBg}
          onDelete={onDelete}
        />
      ))}
      {drafts.length > 0 && hasMore && (
        <Box textAlign='center' mt='16'>
          <Button size='sm' onClick={loadMoreDrafts} isLoading={isLoadingMore}>
            Load more
          </Button>
        </Box>
      )}
    </Box>
  );
}
